import { Box, Typography, LinearProgress, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { RocketLaunch } from '@mui/icons-material';
import { colors, fontSize, sxPresets } from '../../theme';
import { PILLAR_LEVEL_COLOR, RISK_LEVEL_COLOR, fmtDec } from './constants';

// ── Layout primitives ──
export const Card = ({ children, sx = {} }) => (
  <Box sx={{
    ...sxPresets.sectionCard,
    px: 0, py: 1.5, p: undefined,
    ...sx,
  }}>
    {children}
  </Box>
);

export const CardTitle = ({ children, icon: Icon, action }) => (
  <Box sx={{
    ...sxPresets.rowBetween,
    mb: 0, px: 1.5, pb: 1, borderBottom: `1px solid ${colors.gray[100]}`,
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {Icon && (
        <Box sx={{
          width: 24, height: 24, borderRadius: 0.75,
          background: `linear-gradient(135deg, ${colors.info.main}, #6366f1)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon sx={{ fontSize: 14, color: colors.background.paper }} />
        </Box>
      )}
      <Typography sx={{ ...sxPresets.sectionTitle, fontSize: fontSize.base, letterSpacing: '0.01em' }}>
        {children}
      </Typography>
    </Box>
    {action}
  </Box>
);

export const Tile = ({ label, value, sub, color = colors.info.main, Icon }) => (
  <Box sx={{
    bgcolor: colors.background.paper, borderRadius: 1.5,
    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
    border: `1px solid ${colors.gray[100]}`,
    px: 1.5, py: 1.1, display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0,
    transition: 'all .15s ease',
    '&:hover': { borderColor: alpha(color, 0.35), boxShadow: `0 2px 8px ${alpha(color, 0.12)}` },
  }}>
    {Icon && (
      <Box sx={{
        width: 34, height: 34, borderRadius: 1.2, flexShrink: 0,
        background: `linear-gradient(135deg, ${alpha(color, 0.18)}, ${alpha(color, 0.06)})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 17, color }} />
      </Box>
    )}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
        <Typography sx={{ fontSize: fontSize.xs, color: alpha(color, 0.7), fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{label}</Typography>
        <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color, lineHeight: 1.1 }}>{value}</Typography>
      </Box>
      {sub && (
        <Typography sx={{ fontSize: fontSize.xs, color: colors.gray[400], mt: 0.3, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: alpha(color, 0.5), display: 'inline-block', flexShrink: 0 }} />
          {sub}
        </Typography>
      )}
    </Box>
  </Box>
);

export const ScoreBar = ({ value, max = 100, color }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 80 }}>
    <LinearProgress
      variant="determinate"
      value={Math.min(value || 0, max) / max * 100}
      sx={{
        flex: 1, height: 7, borderRadius: 4,
        bgcolor: alpha(color || colors.gray[400], 0.1),
        '& .MuiLinearProgress-bar': {
          bgcolor: color || colors.gray[400], borderRadius: 4,
          backgroundImage: `linear-gradient(90deg, ${alpha(color || colors.gray[400], 0.8)}, ${color || colors.gray[400]})`,
        },
      }}
    />
    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700, color: color || colors.gray[400], minWidth: 28, textAlign: 'right' }}>
      {fmtDec(value, 0)}
    </Typography>
  </Box>
);

// Colored dot + level label. Raw score shown only in tooltip (it's a 0–100 composite, not stop-loss %).
export const RiskBar = ({ value, levelKey, t: tFn }) => {
  if (value == null || !levelKey) {
    return <Typography sx={{ fontSize: fontSize.sm, color: colors.gray[300], fontWeight: 600 }}>—</Typography>;
  }
  const color = RISK_LEVEL_COLOR[levelKey] || colors.gray[400];
  const isVeryHigh = levelKey === 'veryHigh';
  const tooltipTitle = tFn('stockStrength.drawer.riskScoreHint', {
    score: fmtDec(value, 0),
    defaultValue: `Điểm rủi ro kỹ thuật: ${fmtDec(value, 0)}/100`,
  });
  return (
    <Tooltip title={tooltipTitle} arrow placement="top">
      <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5,
        px: isVeryHigh ? 0.75 : 0, py: isVeryHigh ? 0.15 : 0,
        borderRadius: 1,
        bgcolor: isVeryHigh ? alpha(color, 0.1) : 'transparent',
        cursor: 'default',
      }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, boxShadow: `0 0 0 2px ${alpha(color, 0.15)}` }} />
        <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700, color, whiteSpace: 'nowrap', letterSpacing: 0.1 }}>
          {tFn('stockStrength.drawer.riskLevel.' + levelKey)}
        </Typography>
      </Box>
    </Tooltip>
  );
};

// Pillar label — backend supplies the level key; UI only renders color + i18n label.
export const ComponentLabel = ({ value, levelKey, tFn }) => {
  if (!levelKey && value == null) {
    return <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700, color: colors.gray[400] }}>—</Typography>;
  }
  const key = levelKey || 'weak';
  const color = PILLAR_LEVEL_COLOR[key] || colors.gray[400];
  return (
    <Tooltip title={value != null ? `${fmtDec(value, 0)} / 100` : ''} arrow placement="top">
      <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700, color, cursor: 'default' }}>
        {tFn('stockStrength.drawer.pillarLevel.' + key)}
      </Typography>
    </Tooltip>
  );
};

// Breakout-bar badge shown when a stock enters LEADER via pivot breakout bypass.
export const BreakoutBarBadge = ({ t }) => (
  <Tooltip
    title={t('stockStrength.breakoutBar.tooltip', 'Stock just broke out of its consolidation with strong demand and heavy volume.')}
    arrow
    placement="top"
  >
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.3,
      px: 0.6, py: 0.1, borderRadius: 0.8,
      bgcolor: alpha(colors.warning.main, 0.12),
      border: `1px solid ${alpha(colors.warning.main, 0.35)}`,
      cursor: 'help',
    }}>
      <RocketLaunch sx={{ fontSize: 10, color: colors.warning.main }} />
      <Typography sx={{ fontSize: fontSize.xxs, fontWeight: 800, color: colors.warning.dark, textTransform: 'uppercase', letterSpacing: 0.3 }}>
        {t('stockStrength.breakoutBar.label', 'Fresh breakout')}
      </Typography>
    </Box>
  </Tooltip>
);
